(() => {
  const SELECTOR = '[data-eo-sell-my-iphone]';
  const VARIANT_ID_FALLBACK = '57998281703807';

  const CONDITION_COPY = {
    Excellent: {
      title: 'Excellent condition',
      short: 'Excellent cosmetic condition and fully working.',
      intro: 'Select this condition if your device is in excellent cosmetic and working condition.',
      groups: [
        {
          label: 'Cosmetic condition',
          bullets: [
            'Up to 3 very light scratches on the screen.',
            'Up to 5 light scratches on the housing and camera surround.',
            'No cracks, dents, scuffs, chips, missing paint, pressure marks, screen burn, dead pixels, or display defects.'
          ]
        },
        {
          label: 'Functionality',
          bullets: [
            'Handset powers on and is fully functional.',
            'Buttons, Touch ID or Face ID (where fitted), NFC, cameras, speakers, microphones and charging port all function correctly.',
            'No signs of liquid damage.'
          ]
        },
        {
          label: 'Battery and authenticity',
          bullets: [
            'Battery health is a minimum of 80%.',
            'Device is a UK model.',
            'Original software and hardware only.',
            'Device has not been modified, jailbroken, rooted, or repaired with non-genuine parts.'
          ]
        }
      ]
    },
    Good: {
      title: 'Good condition',
      short: 'Visible signs of use but fully functional.',
      intro: 'Select this condition if your device shows visible signs of use but remains fully functional.',
      groups: [
        {
          label: 'Cosmetic condition',
          bullets: [
            'More than 3 light scratches on the screen.',
            'More than 5 light scratches on the housing and camera surround.',
            'Minor dents, scuffs, chips, or areas of missing paint.',
            'Cracked or damaged camera glass may be acceptable, subject to inspection.'
          ]
        },
        {
          label: 'Functionality',
          bullets: [
            'Handset powers on and is fully functional.',
            'Buttons, Touch ID or Face ID (where fitted), NFC, cameras, speakers, microphones and charging port all function correctly.'
          ]
        },
        {
          label: 'Battery and authenticity',
          bullets: [
            'Battery health is a minimum of 80%.',
            'Device is a UK model.',
            'Original software and hardware only.',
            'Device has not been modified, jailbroken, rooted, or repaired with non-genuine parts.'
          ]
        }
      ]
    },
    Faulty: {
      title: 'Faulty condition',
      short: 'Physical damage, functional faults, or repair required.',
      intro: 'Select this condition if your device has physical damage, functional faults, or requires repair.',
      groups: [
        {
          label: 'Physical faults',
          bullets: [
            'Cracked screen, cracked display glass, pressure marks, screen burn, pixel discolouration, dead pixels, or delamination.',
            'Bent chassis or frame, engravings, personal markings, deep chips, gouges, or deep scratches that can be felt with a fingernail.',
            'Heavily scratched or damaged housing requiring replacement.',
            'Dust under the display or dust inside camera lenses.'
          ]
        },
        {
          label: 'Functional faults',
          bullets: [
            'Device does not power on correctly or is not fully functional.',
            'Buttons, Touch ID or Face ID (where fitted), NFC, cameras, speakers, microphones or charging functions do not operate correctly.',
            'Signs of liquid damage.',
            'Battery health below 80%.'
          ]
        },
        {
          label: 'Other issues',
          bullets: [
            'Non-UK model.',
            'Modified software or hardware.',
            'Jailbroken or rooted device.',
            'Devices repaired using non-genuine or incompatible parts.'
          ]
        }
      ]
    }
  };

  const STORAGE_ORDER = {
    '32GB': 32,
    '64GB': 64,
    '128GB': 128,
    '256GB': 256,
    '512GB': 512,
    '1TB': 1024,
    '2TB': 2048
  };

  function parseJsonScript(root, selector, fallback) {
    const el = root.querySelector(selector);
    if (!el) return fallback;
    const text = el.textContent.trim();
    if (!text || text === 'null') return fallback;

    try {
      let value = JSON.parse(text);
      let safety = 0;
      while (typeof value === 'string' && safety < 3) {
        value = JSON.parse(value);
        safety += 1;
      }
      return value;
    } catch (error) {
      console.error('EO Sell My iPhone: JSON parse error', error);
      return fallback;
    }
  }

  function extractRows(raw) {
    if (!raw) return [];

    if (Array.isArray(raw)) {
      if (Array.isArray(raw[1])) return raw[1];
      if (raw[0] && Array.isArray(raw[0].Iphones)) return raw[0].Iphones;
      if (raw[0] && Array.isArray(raw[0].iPhones)) return raw[0].iPhones;
      return raw.filter((row) => row && row.Phone);
    }

    if (Array.isArray(raw.Iphones)) return raw.Iphones;
    if (Array.isArray(raw.iPhones)) return raw.iPhones;
    if (Array.isArray(raw.iphones)) return raw.iphones;
    if (raw.value) return extractRows(raw.value);

    return [];
  }

  function unique(values) {
    return [...new Set(values)].filter(Boolean);
  }

  function storageSort(a, b) {
    const aValue = STORAGE_ORDER[a] || Number.parseInt(a, 10) || 0;
    const bValue = STORAGE_ORDER[b] || Number.parseInt(b, 10) || 0;
    return aValue - bValue;
  }

  function generation(model) {
    const match = String(model).match(/iPhone\s+(\d+)/i);
    return match ? Number.parseInt(match[1], 10) : 0;
  }

  function normalise(value) {
    return String(value || '').replace(/\+/g, ' plus')
      .toLowerCase()
      .replace(/apple/g, '')
      .replace(/refurbished/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  function safeText(value) {
    return value == null ? '' : String(value);
  }

  function button({ className = '', text = '', attrs = {} }) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = className;
    el.textContent = text;
    Object.entries(attrs).forEach(([key, value]) => el.setAttribute(key, value));
    return el;
  }

  function init(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const conditionLabels = parseJsonScript(root, '[data-condition-labels]', {});
    const reviewCopy = parseJsonScript(root, '[data-review-copy]', {});
    const heroHeading = root.querySelector('header h1');
    const heroIntro = root.querySelector('header p');
    const initialHeading = heroHeading.textContent;
    const initialIntro = heroIntro.textContent;
    const rawPricing = parseJsonScript(root, '[data-eo-pricing-json]', null);
    const images = parseJsonScript(root, '[data-eo-product-images]', []);
    const catalogueImages = parseJsonScript(root, '[data-eo-catalogue-images]', {});
    const variantId = root.dataset.variantId || VARIANT_ID_FALLBACK;
    const modelVariants = parseJsonScript(root, '[data-eo-model-variants]', {});

    function getModelVariant(model) {
      if (!Object.prototype.hasOwnProperty.call(modelVariants, model)) return null;
      const variant = modelVariants[model];
      return variant && Number(variant.price) === 0 && variant.available !== false
        && Number.isSafeInteger(Number(variant.id)) && Number(variant.id) > 0 ? variant : null;
    }

    const rows = extractRows(rawPricing).filter((row) => {
      return row && row.Phone && row.Size && row.Excellent && row.Good && row.Faulty;
    });

    const state = {
      screen: 'models',
      model: '',
      storage: '',
      condition: 'Excellent',
      quote: '',
      imei: '',
      paymentMethod: '',
      bankAccountName: '',
      bankSortCode: '',
      bankAccountNumber: '',
      paypalEmail: '',
      submitting: false
    };

    const ui = {
      error: root.querySelector('[data-eo-error]'),
      screens: root.querySelectorAll('[data-screen]'),
      progress: root.querySelector('eo-trade-progress'),
      modelGrid: root.querySelector('[data-model-grid]'),
      selectedImage: root.querySelector('[data-selected-image]'),
      selectedTitle: root.querySelector('[data-selected-title]'),
      storagePills: root.querySelector('[data-storage-pills]'),
      conditionOptions: root.querySelector('[data-condition-options]'),
      conditionDetails: root.querySelector('[data-condition-details]'),
      liveQuote: root.querySelector('[data-live-quote]'),
      sellDevice: root.querySelector('[data-sell-device]'),
      imeiInput: root.querySelector('[data-imei-input]'),
      withImei: root.querySelector('[data-continue-with-imei]'),
      withoutImei: root.querySelector('[data-continue-without-imei]'),
      summaryDevice: root.querySelector('[data-summary-device]'),
      summaryImage: root.querySelector('[data-summary-image]'),
      summaryConfiguration: root.querySelector('[data-summary-configuration]'),
      summaryImei: root.querySelector('[data-summary-imei]'),
      summaryOffer: root.querySelector('[data-summary-offer]'),
      totalSale: root.querySelector('[data-total-sale]'),
      paymentMethods: root.querySelector('[data-payment-methods]'),
      bankFields: root.querySelector('[data-bank-fields]'),
      paypalFields: root.querySelector('[data-paypal-fields]'),
      bankAccountName: root.querySelector('[data-bank-account-name]'),
      bankSortCode: root.querySelector('[data-bank-sort-code]'),
      bankAccountNumber: root.querySelector('[data-bank-account-number]'),
      paypalEmail: root.querySelector('[data-paypal-email]'),
      terms: root.querySelector('[data-trade-terms]'),
      termsLink: root.querySelector('[data-trade-terms-link]'),
      submit: root.querySelector('[data-submit-trade-in]')
    };

    const promotion = window.EOHubTradeBonus?.(root, () => ({ brand: 'iphone', model: state.model, storage: state.storage, condition: state.condition, quote: state.quote }));

    if (!rows.length) {
      showError('Quotes are temporarily unavailable. Please email online@eodistribution.co.uk and we’ll help with your device.');
      return;
    }

    function showError(message) {
      ui.error.textContent = message;
      ui.error.hidden = false;
    }

    function clearError() {
      ui.error.textContent = '';
      ui.error.hidden = true;
    }

    function setScreen(screen) {
      state.screen = screen;
      root.dataset.currentScreen = screen;
      ui.screens.forEach((item) => item.classList.toggle('is-active', item.dataset.screen === screen));
      ui.progress?.setAttribute('current-step', screen);
      heroHeading.textContent = screen === 'summary' ? reviewCopy.heading : initialHeading;
      heroIntro.textContent = screen === 'summary' ? reviewCopy.intro : initialIntro;
      const current = root.querySelector(`[data-screen="${screen}"]`);
      const heading = screen === 'summary' ? heroHeading : current?.querySelector('h2');
      if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
      window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    function getModels() {
      const sourceModels = unique(rows.map((row) => row.Phone));
      const sourceIndex = new Map(sourceModels.map((model, index) => [model, index]));
      return sourceModels.sort((a, b) => {
        const generationDiff = generation(b) - generation(a);
        if (generationDiff !== 0) return generationDiff;
        return sourceIndex.get(a) - sourceIndex.get(b);
      });
    }

    function getStorages(model) {
      return unique(rows.filter((row) => row.Phone === model).map((row) => row.Size)).sort(storageSort);
    }

function handleise(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\.(jpg|jpeg|png|webp)$/i, '')
    .replace(/apple|google|samsung|refurbished|sell-my-iphone|sell-my-galaxy|sell-my-pixel/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getImage(model) {
  const variantImage = getModelVariant(model)?.image;
  if (variantImage) return variantImage;
  const key = handleise(model);

  const fallback = catalogueImages[normalise(model)] || '';
  if (!images.length) return fallback;

  const exactAlt = images.find((image) => handleise(image.alt) === key);
  if (exactAlt) return exactAlt.src;

  const exactFilename = images.find((image) => handleise(image.filename) === key);
  if (exactFilename) return exactFilename.src;

  const filenameWithColour = images.find((image) => {
    const filename = handleise(image.filename);
    const colourSuffixPattern = new RegExp(
      `^${key}-(black|white|blue|pink|purple|green|yellow|red|silver|gold|graphite|midnight|starlight|natural|titanium|deep|sierra|teal|ultramarine)(-|$)`
    );

    return colourSuffixPattern.test(filename);
  });

  return filenameWithColour ? filenameWithColour.src : fallback;
}

    function getMatch() {
      const unlockedMatch = rows.find((row) => row.Phone === state.model && row.Size === state.storage && row.Locked === 'n');
      if (unlockedMatch) return unlockedMatch;
      return rows.find((row) => row.Phone === state.model && row.Size === state.storage);
    }

    function getQuote() {
      const match = getMatch();
      if (!match) return '';
      return safeText(match[state.condition]);
    }

    function updateQuote() {
      const quoteValue = getQuote();
      const quote = window.EOTradeCondition.quote(quoteValue);
      state.quote = quote;
      ui.liveQuote.textContent = quote || conditionLabels.unavailable;
      ui.sellDevice.disabled = !quote;
      const quoteHelp = root.querySelector('[data-quote-help]');
      if (quoteHelp) quoteHelp.hidden = Boolean(quote);
    }

    function renderModels() {
      ui.modelGrid.innerHTML = '';

      getModels().forEach((model) => {
        const card = document.createElement('article');
        card.className = 'eo-device-card';
        card.dataset.model = model.toLowerCase();

        const imageWrap = document.createElement('button');
        imageWrap.type = 'button';
        imageWrap.className = 'eo-device-card__main';
        imageWrap.setAttribute('aria-label', `Select ${model}`);

        const img = document.createElement('img');
        const modelImage = getImage(model);
        if (modelImage) img.src = modelImage;
        img.alt = model;
        img.loading = 'lazy';

        const title = document.createElement('h3');
        title.textContent = model;

        imageWrap.append(img, title);
        imageWrap.addEventListener('click', () => {
          const firstStorage = getStorages(model)[0];
          selectDevice(model, firstStorage);
        });

        const storageWrap = document.createElement('div');
        storageWrap.className = 'eo-device-card__storage';

        getStorages(model).forEach((storage) => {
          const storageButton = button({ text: storage });
          storageButton.addEventListener('click', () => selectDevice(model, storage));
          storageWrap.appendChild(storageButton);
        });

        card.append(imageWrap, storageWrap);
        ui.modelGrid.appendChild(card);
      });
    }

    function selectDevice(model, storage) {
      clearError();
      state.model = model;
      state.storage = storage;
      state.condition = 'Excellent';
      state.quote = '';
      state.imei = '';
      identifier.clear();
      state.paymentMethod = '';
      state.bankAccountName = '';
      state.bankSortCode = '';
      state.bankAccountNumber = '';
      state.paypalEmail = '';
      resetSummaryInputs();
      renderDeviceDetails();
      setScreen('details');
    }

    function renderDeviceDetails() {
      const selectedImage = getImage(state.model);
      ui.selectedImage.hidden = !selectedImage;
      ui.selectedImage.parentElement.hidden = !selectedImage;
      if (selectedImage) ui.selectedImage.src = selectedImage; else ui.selectedImage.removeAttribute('src');
      ui.selectedImage.alt = state.model;
      ui.selectedTitle.textContent = `${state.model} ${state.storage}`;

      renderStoragePills();
      renderConditionButtons();
      renderConditionDetails();
      updateQuote();
    }

    function renderStoragePills() {
      const restoreFocus = ui.storagePills.contains(document.activeElement);
      ui.storagePills.innerHTML = '';
      getStorages(state.model).forEach((storage) => {
        const storageButton = button({ text: storage, className: storage === state.storage ? 'is-selected' : '', attrs: { 'aria-pressed': String(storage === state.storage), 'aria-label': `${state.model} ${storage}` } });
        storageButton.addEventListener('click', () => {
          state.storage = storage;
          renderDeviceDetails();
        });
        ui.storagePills.appendChild(storageButton);
      });
      if (restoreFocus) ui.storagePills.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true });
    }

    function renderConditionButtons() {
      window.EOTradeCondition.renderOptions(ui.conditionOptions, {
        conditions: CONDITION_COPY,
        selected: state.condition,
        prices: getMatch(),
        labels: conditionLabels,
        onSelect(condition) {
          state.condition = condition;
          renderConditionButtons();
          renderConditionDetails();
          updateQuote();
        }
      });
    }

    function renderConditionDetails() {
      window.EOTradeCondition.renderDetails(ui.conditionDetails, CONDITION_COPY[state.condition], conditionLabels);
    }

    function renderSummary() {
      ui.summaryDevice.textContent = state.model;
      ui.summaryConfiguration.textContent = `${state.storage} · ${state.condition}`;
      const image = getImage(state.model);
      ui.summaryImage.hidden = !image;
      if (image) ui.summaryImage.src = image; else ui.summaryImage.removeAttribute('src');
      ui.summaryImage.alt = state.model;
      ui.summaryImei.textContent = state.imei || reviewCopy.notProvided;
      ui.summaryOffer.textContent = state.quote;
      ui.totalSale.textContent = state.quote;
      promotion?.refresh();
      validateSummary();
    }

    function digitsOnly(value, maximumLength) {
      return String(value || '').replace(/\D/g, '').slice(0, maximumLength);
    }

    function formatSortCode(value) {
      return digitsOnly(value, 6).replace(/(\d{2})(?=\d)/g, '$1-');
    }

    function resetSummaryInputs() {
      ui.terms.checked = false;
      if (ui.bankAccountName) ui.bankAccountName.value = '';
      if (ui.bankSortCode) ui.bankSortCode.value = '';
      if (ui.bankAccountNumber) ui.bankAccountNumber.value = '';
      if (ui.paypalEmail) ui.paypalEmail.value = '';
      ui.paymentMethods.querySelectorAll('button').forEach((item) => { item.classList.remove('is-selected'); item.setAttribute('aria-pressed', 'false'); });
      ui.bankFields.hidden = true;
      ui.paypalFields.hidden = true;
    }

    function validateSummary() {
      state.bankAccountName = ui.bankAccountName ? ui.bankAccountName.value.trim() : '';
      state.bankSortCode = ui.bankSortCode ? ui.bankSortCode.value.trim() : '';
      state.bankAccountNumber = ui.bankAccountNumber ? ui.bankAccountNumber.value.trim() : '';
      state.paypalEmail = ui.paypalEmail ? ui.paypalEmail.value.trim() : '';

      const hasBankDetails = state.paymentMethod === 'Bank Transfer' && state.bankAccountName && digitsOnly(state.bankSortCode, 6).length === 6 && digitsOnly(state.bankAccountNumber, 8).length === 8;
      const hasPaypalDetails = state.paymentMethod === 'PayPal' && state.paypalEmail && ui.paypalEmail.checkValidity();
      const hasPaymentDetails = hasBankDetails || hasPaypalDetails;

      ui.submit.disabled = !(!state.submitting && state.quote && state.paymentMethod && hasPaymentDetails && ui.terms.checked);
    }

    function selectCard(container, selectedButton) {
      container.querySelectorAll('button').forEach((item) => { item.classList.remove('is-selected'); item.setAttribute('aria-pressed', 'false'); });
      selectedButton.classList.add('is-selected');
      selectedButton.setAttribute('aria-pressed', 'true');
    }

    function submitTradeIn() {
      if (state.submitting) return;
      validateSummary();
      if (ui.submit.disabled) return;

      state.submitting = true;
      ui.submit.disabled = true;
      ui.submit.textContent = reviewCopy.submitting;

      const selectedVariant = getModelVariant(state.model);
      const properties = {
        [selectedVariant ? '_Model' : 'Model']: state.model,
        Storage: state.storage,
        Condition: state.condition,
        Offer: state.quote,
        'Payment Method': state.paymentMethod,
        '_Trade-in terms': `Accepted: ${ui.termsLink.getAttribute('href')}`
      };

      if (state.imei) properties.IMEI = state.imei;

      if (state.paymentMethod === 'Bank Transfer') {
        properties['Account Holder Name'] = state.bankAccountName;
        properties['Sort Code'] = state.bankSortCode;
        properties['Account Number'] = state.bankAccountNumber;
      }

      if (state.paymentMethod === 'PayPal') {
        properties['PayPal Email Address'] = state.paypalEmail;
      }

      (promotion ? promotion.prepare(properties) : Promise.resolve(properties))
        .then(requestProperties => fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(selectedVariant?.id || variantId),
          quantity: 1,
          properties: requestProperties
        })
      }))
        .then((response) => {
          if (!response.ok) throw new Error('Add to cart failed');
          window.location.href = (window.Shopify?.routes?.root || '/') + 'checkout';
        })
        .catch((error) => {
          console.error('EO Sell My iPhone submit error', error);
          state.submitting = false;
          validateSummary();
          ui.submit.textContent = reviewCopy.checkout;
          showError(error.userMessage || 'Something went wrong while creating your Trade In. Please try again.');
        });
    }

    const identifier = window.EOTradeIdentifier(root, ui.imeiInput, ui.withImei);

    ui.sellDevice.addEventListener('click', () => {
      if (state.quote) identifier.open(state.imei);
    });

    root.querySelector('[data-imei-form]').addEventListener('submit', event => {
      event.preventDefault();
      if (!identifier.validate(true)) { ui.imeiInput.focus(); return; }
      state.imei = identifier.value();
      identifier.close();
      renderSummary();
      setScreen('summary');
    });

    ui.withoutImei.addEventListener('click', () => {
      state.imei = '';
      identifier.clear();
      identifier.close();
      renderSummary();
      setScreen('summary');
    });

    root.querySelector('[data-edit-imei]').addEventListener('click', () => identifier.open(state.imei));
    root.querySelector('[data-edit-device]').addEventListener('click', () => setScreen('details'));

    ui.paymentMethods.addEventListener('click', (event) => {
      const target = event.target.closest('button[data-payment]');
      if (!target) return;
      state.paymentMethod = target.dataset.payment;
      selectCard(ui.paymentMethods, target);
      ui.bankFields.hidden = state.paymentMethod !== 'Bank Transfer';
      ui.paypalFields.hidden = state.paymentMethod !== 'PayPal';
      validateSummary();
    });

    if (ui.bankSortCode) {
      ui.bankSortCode.addEventListener('input', () => {
        ui.bankSortCode.value = formatSortCode(ui.bankSortCode.value);
      });
    }

    if (ui.bankAccountNumber) {
      ui.bankAccountNumber.addEventListener('input', () => {
        ui.bankAccountNumber.value = digitsOnly(ui.bankAccountNumber.value, 8);
      });
    }

    [
      ui.bankAccountName,
      ui.bankSortCode,
      ui.bankAccountNumber,
      ui.paypalEmail
    ].filter(Boolean).forEach((input) => input.addEventListener('input', validateSummary));

    ui.terms.addEventListener('change', validateSummary);
    ui.progress?.addEventListener('eo:trade-step', event => {
      if (state.submitting) return;
      const step = event.detail.step;
      if (step === 'models' || (step === 'details' && state.model && state.storage)) setScreen(step);
    });
    ui.submit.addEventListener('click', submitTradeIn);

    root.querySelector('[data-back-to-models]').addEventListener('click', () => setScreen('models'));
    root.querySelector('[data-back-to-details]').addEventListener('click', () => setScreen('details'));

    renderModels();
    const search = root.querySelector('[data-eo-model-search]');
    search?.addEventListener('input', () => {
      const term = search.value.trim().toLowerCase();
      const cards = [...ui.modelGrid.querySelectorAll('[data-model]')];
      cards.forEach((card) => { card.hidden = !card.dataset.model.includes(term); });
      root.querySelector('[data-eo-model-empty]').hidden = cards.some((card) => !card.hidden);
    });
  }

  const initialise = (root = document) => root.querySelectorAll(SELECTOR).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
